import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { FileText, Tag, Calendar } from 'lucide-react';

interface Pet {
  id: string;
  name: string;
  species: string;
  breed?: string;
}

interface IdentitySectionProps {
  data: Record<string, any>;
  pet: Pet;
  onChange: (data: Record<string, any>) => void;
}

export const IdentitySection = ({ data, pet, onChange }: IdentitySectionProps) => {
  const handleChange = (field: string, value: any) => {
    onChange({
      ...data,
      [field]: value
    });
  };

  const species = [
    'Cane', 'Gatto', 'Coniglio', 'Uccello', 'Pesce', 
    'Tartaruga', 'Criceto', 'Furetto', 'Cavallo', 'Altro'
  ];

  const genders = ['Maschio', 'Femmina', 'Non specificato'];
  
  const reproductiveStatus = ['Intero', 'Sterilizzato', 'Castrato', 'Non specificato'];

  return (
    <div className="space-y-6 pb-8">
      <div className="flex items-center gap-3 mb-6">
        <FileText className="w-6 h-6 text-blue-500" />
        <h2 className="text-2xl font-bold text-white">Informazioni Anagrafiche</h2>
      </div>

      {/* Basic Information */}
      <Card className="glass border-white/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white">
            <Tag className="w-5 h-5" />
            Dati Base
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="pet_name" className="text-white">Nome</Label>
              <Input
                id="pet_name"
                value={data.name || pet.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Nome del pet"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="aliases" className="text-white">Soprannomi</Label>
              <Input
                id="aliases"
                value={data.aliases?.join(', ') || ''}
                onChange={(e) => handleChange('aliases', e.target.value.split(',').map((s: string) => s.trim()).filter(Boolean))}
                placeholder="Separati da virgola"
                className="mt-1"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="species" className="text-white">Specie</Label>
              <Select value={data.species || pet.species} onValueChange={(value) => handleChange('species', value)}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Seleziona specie" />
                </SelectTrigger>
                <SelectContent>
                  {species.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="breed" className="text-white">Razza</Label>
              <Input
                id="breed"
                value={data.breed || pet.breed || ''}
                onChange={(e) => handleChange('breed', e.target.value)}
                placeholder="Razza"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="sex" className="text-white">Sesso *</Label>
              <Select value={data.sex || ''} onValueChange={(value) => handleChange('sex', value)}>
                <SelectTrigger className={`mt-1 ${!data.sex ? 'border-red-500/50' : ''}`}>
                  <SelectValue placeholder="Seleziona sesso" />
                </SelectTrigger>
                <SelectContent>
                  {genders.map((g) => (
                    <SelectItem key={g} value={g}>{g}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!data.sex && (
                <p className="text-red-400 text-xs mt-1">Campo obbligatorio</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="date_of_birth" className="text-white">Data di Nascita</Label>
              <Input
                id="date_of_birth"
                type="date"
                value={data.date_of_birth || ''}
                onChange={(e) => handleChange('date_of_birth', e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="reproductive_status" className="text-white">Stato Riproduttivo</Label>
              <Select value={data.reproductive_status || ''} onValueChange={(value) => handleChange('reproductive_status', value)}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Seleziona stato" />
                </SelectTrigger>
                <SelectContent>
                  {reproductiveStatus.map((status) => (
                    <SelectItem key={status} value={status}>{status}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Physical Characteristics */}
      <Card className="glass border-white/20">
        <CardHeader>
          <CardTitle className="text-white">Caratteristiche Fisiche</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="weight_kg" className="text-white">Peso (kg)</Label>
              <Input
                id="weight_kg"
                type="number"
                step="0.1"
                value={data.physical?.weight_kg || ''}
                onChange={(e) => handleChange('physical', { ...data.physical, weight_kg: parseFloat(e.target.value) || 0 })}
                placeholder="0.0"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="height_cm" className="text-white">Altezza (cm)</Label>
              <Input
                id="height_cm"
                type="number"
                value={data.physical?.height_withers_cm || ''}
                onChange={(e) => handleChange('physical', { ...data.physical, height_withers_cm: parseInt(e.target.value) || 0 })}
                placeholder="0"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="bcs" className="text-white">Body Condition Score (1-9)</Label>
              <Input
                id="bcs"
                type="number"
                min="1"
                max="9"
                value={data.physical?.bcs || ''}
                onChange={(e) => handleChange('physical', { ...data.physical, bcs: parseInt(e.target.value) || 0 })}
                placeholder="5"
                className="mt-1"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="color_markings" className="text-white">Colore e Segni Particolari</Label>
            <Textarea
              id="color_markings"
              value={data.color_markings || ''}
              onChange={(e) => handleChange('color_markings', e.target.value)}
              placeholder="Descrivi colore del mantello e segni distintivi..."
              className="mt-1"
              rows={3}
            />
          </div>

          <div>
            <Label htmlFor="identifying_marks" className="text-white">Segni di Riconoscimento</Label>
            <Textarea
              id="identifying_marks"
              value={data.identifying_marks || ''}
              onChange={(e) => handleChange('identifying_marks', e.target.value)}
              placeholder="Cicatrici, tatuaggi, anelli o altri segni identificativi..."
              className="mt-1"
              rows={2}
            />
          </div>
        </CardContent>
      </Card>

      {/* Microchip Information */}
      <Card className="glass border-white/20">
        <CardHeader>
          <CardTitle className="text-white">Identificazione Elettronica</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="microchip_code" className="text-white">Codice Microchip</Label>
              <Input
                id="microchip_code"
                value={data.microchip?.code || ''}
                onChange={(e) => handleChange('microchip', { ...data.microchip, code: e.target.value })}
                placeholder="15 cifre"
                maxLength={15}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="implant_date" className="text-white">Data Impianto</Label>
              <Input
                id="implant_date"
                type="date"
                value={data.microchip?.implant_date || ''}
                onChange={(e) => handleChange('microchip', { ...data.microchip, implant_date: e.target.value })}
                className="mt-1"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="implant_site" className="text-white">Sede Impianto</Label>
              <Input
                id="implant_site"
                value={data.microchip?.implant_site || ''}
                onChange={(e) => handleChange('microchip', { ...data.microchip, implant_site: e.target.value })}
                placeholder="es. Collo lato sinistro"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="veterinarian" className="text-white">Veterinario</Label>
              <Input
                id="veterinarian"
                value={data.microchip?.veterinarian || ''}
                onChange={(e) => handleChange('microchip', { ...data.microchip, veterinarian: e.target.value })}
                placeholder="Nome del veterinario"
                className="mt-1"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Passport Numbers */}
      <Card className="glass border-white/20">
        <CardHeader>
          <CardTitle className="text-white">Documenti Ufficiali</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="national_passport" className="text-white">Passaporto Nazionale</Label>
              <Input
                id="national_passport"
                value={data.passport_numbers?.national_passport_no || ''}
                onChange={(e) => handleChange('passport_numbers', { ...data.passport_numbers, national_passport_no: e.target.value })}
                placeholder="Numero passaporto"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="eu_passport" className="text-white">Passaporto EU</Label>
              <Input
                id="eu_passport"
                value={data.passport_numbers?.eu_pet_passport_no || ''}
                onChange={(e) => handleChange('passport_numbers', { ...data.passport_numbers, eu_pet_passport_no: e.target.value })}
                placeholder="Solo per cani/gatti/furetti"
                className="mt-1"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Owner Information */}
      <Card className="glass border-white/20">
        <CardHeader>
          <CardTitle className="text-white">Informazioni Proprietario</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="owner_name" className="text-white">Nome Proprietario</Label>
              <Input
                id="owner_name"
                value={data.owner?.name || ''}
                onChange={(e) => handleChange('owner', { ...data.owner, name: e.target.value })}
                placeholder="Nome e cognome"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="owner_phone" className="text-white">Telefono</Label>
              <Input
                id="owner_phone"
                value={data.owner?.phone || ''}
                onChange={(e) => handleChange('owner', { ...data.owner, phone: e.target.value })}
                placeholder="Numero di telefono"
                className="mt-1"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="owner_address" className="text-white">Indirizzo</Label>
            <Textarea
              id="owner_address"
              value={data.owner?.address || ''}
              onChange={(e) => handleChange('owner', { ...data.owner, address: e.target.value })}
              placeholder="Via, numero civico, città, CAP, provincia"
              className="mt-1"
              rows={3}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="owner_email" className="text-white">Email</Label>
              <Input
                id="owner_email"
                type="email"
                value={data.owner?.email || ''}
                onChange={(e) => handleChange('owner', { ...data.owner, email: e.target.value })}
                placeholder="indirizzo@email.com"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="emergency_contact" className="text-white">Contatto Emergenza</Label>
              <Input
                id="emergency_contact"
                value={data.owner?.emergency_contact || ''}
                onChange={(e) => handleChange('owner', { ...data.owner, emergency_contact: e.target.value })}
                placeholder="Nome e telefono"
                className="mt-1"
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};